// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React, { type Node } from 'react';

import { keyframes } from '@emotion/core';
import { Animation, Colors, Shadows, Typography } from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

/** @jsx isolateComponent */

export type TooltipPosition =
  | 'northwest'
  | 'north'
  | 'northeast'
  | 'east'
  | 'southeast'
  | 'south'
  | 'southwest'
  | 'west';

type Props = {
  /**
   * Indicates whether the content is hidden from screenreaders.
   * This is useful when the `Tooltip` has already been associated
   * to a node via `aria-describedby` to prevent screenreaders from
   * reading the content of the `Tooltip` twice. <strong>Note that
   * some screenreaders like VoiceOver only read content associated
   * via `aria-describedby` when the element with that attribute is
   * interactive, like a `button` element.</strong>
   */
  ariaHidden?: boolean,
  /** The content to render inside of the Tooltip. */
  children: Node,
  /** The position to place the tooltip relative to the parent element */
  position: TooltipPosition,
  /** Unique id, intended for use with `aria-describedby` which is why it's required */
  id: string,
};

const distance = '5px';

const tooltipOpenAnimation = keyframes({
  '0%': {
    opacity: 0,
  },
  '100%': {
    opacity: 1,
  },
});

const positionStyles = {
  northwest: {
    bottom: `calc(100% + ${distance})`,
    left: 0,
  },

  north: {
    bottom: `calc(100% + ${distance})`,
    left: '50%',
    transform: 'translateX(-50%)',
  },

  northeast: {
    bottom: `calc(100% + ${distance})`,
    right: 0,
  },

  east: {
    left: `calc(100% + ${distance})`,
    top: '50%',
    transform: 'translateY(-50%)',
  },

  southeast: {
    top: `calc(100% + ${distance})`,
    right: 0,
  },

  south: {
    top: `calc(100% + ${distance})`,
    left: '50%',
    transform: 'translateX(-50%)',
  },

  southwest: {
    top: `calc(100% + ${distance})`,
    left: 0,
  },

  west: {
    right: `calc(100% + ${distance})`,
    top: '50%',
    transform: 'translateY(-50%)',
  },
};

const Styles = labelStyles(__filename, {
  root: (props: Props) => [
    {
      color: Colors.white,
      textAlign: 'center',
      borderRadius: 100,
      position: 'absolute',
      padding: '0 1em',
      height: 20,
      zIndex: 1,
      boxShadow: Shadows.elevation5,
      backgroundColor: Colors.grey900,
      maxWidth: 300,
      animation: `${Animation.transitionDuration} ${Animation.transitionTimingFunction} ${tooltipOpenAnimation}`,
      ...Typography.altText900,
      fontWeight: 600,

      // Increase mouse area so tooltip doesn't disappear when
      // mousing over a gap between its toggle and the tooltip itself.
      '&::before': {
        content: '""',
        position: 'absolute',
        top: `-${distance}`,
        right: `-${distance}`,
        bottom: `-${distance}`,
        left: `-${distance}`,
        zIndex: -1,
      },
    },

    withModifier(props.position, positionStyles[props.position]),
  ],

  // Separate node to truncate content. It's important
  // to apply `overflow: hidden` here and not on `root`
  // otherwise the `::before` will be clipped.
  content: {
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
});

/**
 * Tooltip component, a floating tooltip to display additional information.
 * Can be used on its own, or rendered by the TooltipWrapper.
 *
 * Tags: core-ui
 */
const Tooltip = (props: Props) => {
  return (
    <span
      aria-hidden={props.ariaHidden}
      css={Styles.root(props)}
      data-testid={`tooltip-${props.position}`}
      id={props.id}
      role="tooltip"
    >
      <span css={Styles.content}>{props.children}</span>
    </span>
  );
};

Tooltip.defaultProps = {
  position: 'south',
};

export default Tooltip;
